import React, { useEffect, useState } from 'react';
import { FaArchive, FaEdit } from 'react-icons/fa';
import { AiOutlineArrowUp, AiOutlineArrowDown } from 'react-icons/ai';
import axios from "axios";
import { Pagination, Button, Modal, Tooltip, OverlayTrigger } from 'react-bootstrap';
import { toast } from 'react-toastify';
import '../assets/table.css'; 
import AddUnitModal from '../components/Add/AddUnitModal';
import EditUnitModal from '../components/Edit/EditUnitModal';

const UnitOfMeasurement = () => {
    const [units, setUnits] = useState([]);
    const [originalUnits, setOriginalUnits] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [unitsPerPage, setUnitsPerPage] = useState(5);
    const [sortBy, setSortBy] = useState({ key: 'id', order: 'asc' });
    const [showAddModal, setShowAddModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [unitIdToDelete, setUnitIdToDelete] = useState(null);
    const [editUnit, setEditUnit] = useState({});
    const [editLoading, setEditLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState('');
    const [userRole, setUserRole] = useState(null);
    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

    useEffect(() => {
        const role = parseInt(localStorage.getItem('userRole'), 10);
        setUserRole(role);
        getUnits();
    }, []);

    const handleCloseAddModal = () => setShowAddModal(false);
    const handleShowAddModal = () => setShowAddModal(true);

    const handleCloseDeleteModal = () => setShowDeleteModal(false);
// When the user clicks on the "Delete" button, pass the unit ID
const handleShowDeleteModal = (unitId) => {
    setUnitIdToDelete(unitId);
    setShowDeleteModal(true);
};


    const handleCloseEditModal = () => setShowEditModal(false);

    const handleShowEditModal = (unitId) => {    
        setErrorMessage('');
        setEditLoading(true);
        axios.get(`${API_BASE_URL}/api/units.php/${unitId}`)
            .then(response => {
                // Adjusted to match the API response structure
                if (response.data.unit && response.data.unit.id) {
                    setEditUnit(response.data.unit);  // Corrected response structure here
                    setEditLoading(false);
                    setShowEditModal(true);
                } else {
                    console.error('Error: Unit not found', response.data);
                    setEditLoading(false);
                }
            })
            .catch(error => {
                console.error('Error fetching unit:', error);
                setEditLoading(false);
            });
    };
    
    

    const getUnits = () => {
        axios.get(`${API_BASE_URL}/api/units.php?archived=0`) // Fetch only active units
        .then(response => {
            if (Array.isArray(response.data.units)) {
                const fetchedUnits = response.data.units;
                const sortedUnits = sortUnits(fetchedUnits, 'id', 'asc');
                setUnits(sortedUnits);
                setOriginalUnits(fetchedUnits);
            } else {
                console.error('Unexpected response structure: units is not an array');
                setUnits([]);
                setOriginalUnits([]);
            }
        });
        
    };
    
    
    const sortUnits = (units, key, order) => {
        return [...units].sort((a, b) => {
            if (key === 'id') {
                return order === 'asc' ? a.id - b.id : b.id - a.id;
            }

            const valueA = typeof a[key] === 'string' ? a[key].toLowerCase() : a[key];
            const valueB = typeof b[key] === 'string' ? b[key].toLowerCase() : b[key];

            if (valueA < valueB) {
                return order === 'asc' ? -1 : 1;
            }
            if (valueA > valueB) {
                return order === 'asc' ? 1 : -1;
            }
            return 0;
        });
    };

    const handleFilter = (event) => {
        const searchText = event.target.value.toLowerCase();
        const newData = originalUnits.filter(row => 
            String(row.unit_name).toLowerCase().includes(searchText) ||
            String(row.created_by).toLowerCase().includes(searchText)
        );

        if (searchText) {
            setUnits(newData);
        } else {
            const sorted = sortUnits(originalUnits, sortBy.key, sortBy.order);
            setUnits(sorted);
        }
    };

    const indexOfLastUnit = currentPage * unitsPerPage;
    const indexOfFirstUnit = indexOfLastUnit - unitsPerPage;
    const currentUnits = units.slice(indexOfFirstUnit, indexOfLastUnit);

    const paginate = (pageNumber) => setCurrentPage(pageNumber);

    const handlePerPageChange = (e) => {
        setCurrentPage(1);
        setUnitsPerPage(Number(e.target.value));
    };

    const handleSort = (key) => {
        let order = 'asc';
        if (sortBy.key === key && sortBy.order === 'asc') {
            order = 'desc';
        }
        setSortBy({ key: key, order: order });
        const sortedUnits = sortUnits(units, key, order);
        setUnits(sortedUnits);
    };

    const getSortIcon = (key) => {
        if (sortBy.key === key) {
            return sortBy.order === 'asc' ? <AiOutlineArrowUp /> : <AiOutlineArrowDown />;
        }
        return null;
    };

    const handleUnitAdded = () => {                    
        toast.success('Unit of Measurement added successfully!');
        getUnits(); // Only refetch units after unit is added, no need for a toast here
    };
    
    const handleEditChange = (event) => {
        const { name, value } = event.target;
        setEditUnit((prevUnit) => ({ ...prevUnit, [name]: value })); // Fix this line
    };
    
    const handleEditSubmit = (event) => {
        event.preventDefault();
   
        // Log the current state of editUnit to debug
        console.log("Submitting update for unit:", editUnit);
        
        if (!editUnit.unit_name.trim()) {
            setErrorMessage('Unit name cannot be empty.');
            toast.error('Unit name cannot be empty!');
            return;
        }
   
        // Get user ID from localStorage
        const userId = localStorage.getItem('userID') || null;
   
        // Prepare updated data with `updated_by`
        const updatedUnit = {
            ...editUnit,
            updated_by: userId, // Added `updated_by`
        };
   
        // Send PUT request for updating unit name
        axios.put(`${API_BASE_URL}/api/units.php/${editUnit.id}`, updatedUnit)
            .then((response) => {
                console.log("API response:", response);  // Log API response for debugging
                if (response.data.status === 1) {
                    handleCloseEditModal();
                    getUnits();  // This triggers the re-fetch, so we avoid calling toast here again
                    toast.success('Unit updated successfully!');
                } else {
                    console.error("Update Failed:", response.data);
                    setErrorMessage(response.data.message || 'Failed to update unit');
                    toast.error(response.data.message || 'Failed to update unit! 🚨');
                }
            })
            .catch((error) => {
                console.error("Error updating unit:", error);
                setErrorMessage('Failed to update unit. Please try again.');
                toast.error('Error updating unit! 🚨');
            });
    };
   

    const archiveUnit = (unitId) => {
        if (!unitId) {
            toast.error('Invalid unit ID.'); // Prevent sending an invalid request
            return;
        }
    
        // Ensure you're only sending the relevant data (id and archived)
        console.log("Archiving unit with ID:", unitId);  // Debugging: Check unitId value
    
        axios.put(`${API_BASE_URL}/api/units.php/${unitId}`, { 
            id: unitId, 
            archived: 1  // Ensure archived is correctly set to 1
        })
        .then((response) => {
            console.log("API response:", response);  // Log API response for debugging
            if (response.data.status === 1) {
                getUnits(); // Refresh the list after archiving
                handleCloseDeleteModal();
                toast.success('Unit archived successfully!');
            } else {
                toast.warning(response.data.message || 'No changes made.');
            }
        })
        .catch((error) => {
            console.error('Error archiving unit:', error);
            toast.error('Failed to archive unit.');
        });
    };
    
    return (
        <div className='container mt-2'>
            <h1 style={{ textAlign: 'left', fontWeight: 'bold' }}>Unit of Measurement</h1>
            <div className='d-flex justify-content-between align-items-center'>
                <div className="input-group" style={{ width: '25%' }}>
                    <input type="text" className="form-control shadow-sm" onChange={handleFilter} placeholder="Search" />
                </div>
                {userRole !== 1 && (
                    <div className='text-end'>
                        <Button onClick={handleShowAddModal} className='btn btn-primary w-100 btn-gradient'
                        style={{
                            backgroundImage: 'linear-gradient(to right, #006cb6, #31b44b)',
                            color: '#ffffff',
                            borderColor: '#006cb6',
                            fontWeight: 'bold',
                            marginBottom: '-10px'
                        }}>
                            Add Unit
                        </Button>
                    </div>
                )}
            </div>

            <div className="table-responsive">
                <table className="table table-striped table-hover shadow-sm custom-table align-middle" style={{ width: '100%' }}>
                    <thead className="table-light">
                        <tr>
                            <th className="text-center" onClick={() => handleSort('id')}>
                                #
                                {getSortIcon('id')}
                            </th>
                            <th className="text-center" onClick={() => handleSort('unit_name')}>
                                Unit Name
                                {getSortIcon('unit_name')}
                            </th>
                            {userRole !== 1 && (
                                <th className="text-center">Action</th>
                            )}
                        </tr>
                    </thead>
                    <tbody>
                        {currentUnits.map((unit, index) => {
                            const recentIndex = index + indexOfFirstUnit + 1; 
                            return (
                                <tr key={unit.id}>
                                    <td className="text-center">{recentIndex}</td>
                                    <td className="text-center">{unit.unit_name}</td>
                                    {userRole !== 1 && (
                                        <td className="text-center">
                                            <OverlayTrigger placement="top" overlay={<Tooltip>Edit</Tooltip>}>
                                                <Button 
                                                    onClick={() => handleShowEditModal(unit.id)}
                                                    className="btn btn-primary me-2" 
                                                >
                                                    <FaEdit />
                                                </Button>
                                            </OverlayTrigger>
                                            <OverlayTrigger placement="top" overlay={<Tooltip>Archive</Tooltip>}>
                                                <Button 
                                                    onClick={() => handleShowDeleteModal(unit.id)}
                                                    className="btn btn-warning"
                                                >
                                                    <FaArchive />
                                                </Button>
                                            </OverlayTrigger>
                                        </td>
                                    )}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            <div className="d-flex justify-content-between align-items-center">
            {/* Items per page selector (left) */}
                <div className="d-flex align-items-center">
                    <label className="me-2 fw-bold">Items per page:</label>
                    <select 
                        className="form-select form-select-sm shadow-sm" 
                        style={{ width: '80px' }} 
                        value={unitsPerPage} 
                        onChange={handlePerPageChange}
                    >
                        <option value={5}>5</option>
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                        <option value={50}>50</option>
                    </select>
                </div>
                    <Pagination className='mb-0'>
                        {Array.from({ length: Math.ceil(units.length / unitsPerPage) }, (_, index) => index + 1).map((pageNumber) => (
                            <Pagination.Item
                                key={pageNumber}
                                active={pageNumber === currentPage}
                                onClick={() => paginate(pageNumber)}
                            >
                                {pageNumber}
                            </Pagination.Item>
                        ))}
                    </Pagination>
                </div>

            <AddUnitModal
                show={showAddModal}
                onClose={handleCloseAddModal}
                onUnitAdded={handleUnitAdded}
            />

            <EditUnitModal
                show={showEditModal}
                handleClose={handleCloseEditModal}
                editUnit={editUnit}
                loading={editLoading}
                handleEditChange={handleEditChange} 
                handleEditSubmit={handleEditSubmit}
                errorMessage={errorMessage}
            />

        <Modal show={showDeleteModal} onHide={handleCloseDeleteModal}>
            <Modal.Header closeButton>
                <Modal.Title>Archive Unit</Modal.Title>
            </Modal.Header>
            <Modal.Body>Are you sure you want to archive this unit?</Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleCloseDeleteModal}>
                    Cancel
                </Button>
                <Button 
                    variant="danger" 
                    onClick={() => archiveUnit(unitIdToDelete)} // Pass the unitId here
                >
                    Archive
                </Button>
            </Modal.Footer>
        </Modal>
        </div>
    );
};

export default UnitOfMeasurement;
